import {
  Title,
  parseUrlHelper,
  Button,
  useModals,
  Stack,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { parseUrlAppHelper } from "@/helpers/parse-url.helper.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HiringPlanTable } from "@/pages/recruit/hiring-plan/components/HiringPlanTable.tsx";
import { RequestPositionEditModal } from "@/pages/recruit/hiring-plan/widgets/RequestPositionEditModal.tsx";
import { useEmployeeHiringPlanSearch } from "@/queries/employees/use-employees.query.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ApiHiringPlanFilter, ApiHiringPlanStatus } from "@/types/recruit/hiring-plan.types.ts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { EmployeeWorkflowApprovalRequests } from "@/pages/employees/employee/components/EmployeeWorkflowApprovalRequests.tsx";

export function EmployeePositionRequests({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { isFeatureEnabled, canIView } = usePermissions();
  const [searchParams] = useSearchParams();
  const canViewPositionRequests = isFeatureEnabled(ApiFeatureType.ats_hiring_plan);
  const canViewApprovalRequests =
    isFeatureEnabled(ApiFeatureType.approvals) && isFeatureEnabled(ApiFeatureType.workflows);
  const [filter, setFilter] = useState<ApiHiringPlanFilter>({
    page_number: 1,
    division_ids: parseUrlHelper.getNumberArray(searchParams, "division_ids"),
    department_ids: parseUrlHelper.getNumberArray(searchParams, "department_ids"),
    location_ids: parseUrlHelper.getNumberArray(searchParams, "location_ids"),
    date_plan_to: parseUrlAppHelper.getDate(searchParams, "date_plan_to"),
    date_plan_from: parseUrlAppHelper.getDate(searchParams, "date_plan_from"),
    created_by_ids: [employee.id],
    statuses: parseUrlAppHelper.getEnumArray(searchParams, "statuses", ApiHiringPlanStatus),
  });
  const {
    data: hiringPlan,
    isLoading,
    isFetching,
  } = useEmployeeHiringPlanSearch(employee.id, filter, canViewPositionRequests);

  return (
    <Stack gap="xl">
      {canViewApprovalRequests && <EmployeeWorkflowApprovalRequests employee={employee} />}

      {canViewPositionRequests && (
        <Stack gap="lg">
          <Stack justify={"between"} direction={"horizontal"} items={"center"} gap={"md"}>
            <Title
              header={t("hiring_plan:position_requests")}
              size="lg"
              badge={
                hiringPlan?.metadata.total_items
                  ? `(${hiringPlan.metadata.total_items})`
                  : undefined
              }
              badgeVariant="secondary"
            />
            <Stack direction={"horizontal"} gap={"sm"}>
              {canIView(ApiPermission.recruiting_module) && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                  onClick={() => {
                    openModal(RequestPositionEditModal);
                  }}
                >
                  {t("common:create_new")}
                </Button>
              )}
            </Stack>
          </Stack>
          <HiringPlanTable
            items={hiringPlan?.items ?? []}
            isLoading={isLoading}
            isFetching={isFetching}
            onPageChange={(page) => {
              setFilter({ ...filter, page_number: page });
            }}
            isFiltering={false}
            metadata={hiringPlan?.metadata}
            defaultVisibleRecords={4}
          />
        </Stack>
      )}
    </Stack>
  );
}
